import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import {
  FaChair,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaInfoCircle,
  FaLock,
  FaMobileAlt,
  FaPhoneAlt,
  FaRegCalendarAlt,
  FaTicketAlt,
  FaUser,
  FaUserShield,
} from "react-icons/fa";
import Header from "../components/Header";
import NoticeBar from "../components/NoticeBar";

const MAX_SEATS_PER_BOOKING = 10;
const SHOW_TITLE = "Benton Theatres";
const HUB_SUBTITLE = "Support & Booking Hub";
const BANNER_URL =
  "https://benton.lightideaproductions.co.uk/online-bookings-banner.png";

const HUB_TABS = {
  CONTACT: "contact",
  REPORT: "report",
  TICKETS: "tickets",
};

const SEATING_CONFIG = [
  { row: "A", count: 10 },
  { row: "B", count: 12 },
  { row: "C", count: 14 },
  { row: "D", count: 18 },
  { row: "E", count: 18 },
  { row: "F", count: 18 },
  { row: "G", count: 18 },
  { row: "H", count: 18 },
  { row: "I", count: 18 },
  { row: "J", count: 18 },
  { row: "K", count: 18 },
  { row: "L", count: 18 },
  { row: "M", count: 18 },
];

function buildSeatLayout() {
  return SEATING_CONFIG.map(({ row, count }) => ({
    row,
    seats: Array.from({ length: count }, (_, i) => `${row}${i + 1}`),
  }));
}

function formatDisplayDate(dateString) {
  if (!dateString) return "";
  const d = new Date(`${dateString}T12:00:00`);
  return d.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatTimeDisplay(timeString) {
  if (!timeString) return "";
  const [hours, minutes] = String(timeString).split(":");
  return `${hours}:${minutes}`;
}

function sortSeatIds(seats) {
  return [...seats].sort((a, b) => {
    const rowA = a[0];
    const rowB = b[0];
    const numA = Number(a.slice(1));
    const numB = Number(b.slice(1));

    if (rowA === rowB) return numA - numB;
    return rowA.localeCompare(rowB);
  });
}

export default function Home() {
  const [activeTab, setActiveTab] = useState(HUB_TABS.CONTACT);

  const [shows, setShows] = useState([]);
  const [selectedShowId, setSelectedShowId] = useState("");
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [bookedSeatIds, setBookedSeatIds] = useState([]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [showsLoading, setShowsLoading] = useState(true);
  const [availabilityError, setAvailabilityError] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [ticketForm, setTicketForm] = useState({
    robloxUsername: "",
    email: "",
  });

  const [contactForm, setContactForm] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactMessage, setContactMessage] = useState("");
  const [contactError, setContactError] = useState("");

  const [reportForm, setReportForm] = useState({
    reporterName: "",
    reporterEmail: "",
    staffMemberName: "",
    incidentDate: "",
    category: "",
    details: "",
    evidenceLinks: "",
  });
  const [reportLoading, setReportLoading] = useState(false);
  const [reportMessage, setReportMessage] = useState("");
  const [reportError, setReportError] = useState("");

  const [isLandscapeRequired, setIsLandscapeRequired] = useState(false);
  const [seatScale, setSeatScale] = useState(1);

  const seatingLayout = useMemo(() => buildSeatLayout(), []);
  const totalSeats = useMemo(
    () => seatingLayout.reduce((acc, row) => acc + row.seats.length, 0),
    [seatingLayout],
  );

  const maxRowSeats = useMemo(
    () => Math.max(...seatingLayout.map((row) => row.seats.length)),
    [seatingLayout],
  );

  const selectedShow = useMemo(
    () =>
      shows.find((show) => String(show.id) === String(selectedShowId)) || null,
    [shows, selectedShowId],
  );

  const bookedSeatSet = useMemo(() => new Set(bookedSeatIds), [bookedSeatIds]);
  const selectedSeatSet = useMemo(
    () => new Set(selectedSeats),
    [selectedSeats],
  );

  const availableSeatCount = totalSeats - bookedSeatIds.length;

  const fetchShows = useCallback(async () => {
    setShowsLoading(true);

    const { data, error } = await supabase
      .from("shows")
      .select("id, show_name, show_date, show_time, enabled")
      .eq("enabled", true)
      .order("show_date", { ascending: true })
      .order("show_time", { ascending: true });

    if (error) {
      console.error("Failed to fetch shows:", error);
      setShows([]);
      setSelectedShowId("");
      setShowsLoading(false);
      return;
    }

    const safeShows = data || [];
    setShows(safeShows);

    if (safeShows.length > 0) {
      setSelectedShowId((prev) => prev || String(safeShows[0].id));
    } else {
      setSelectedShowId("");
    }

    setShowsLoading(false);
  }, []);

  const fetchBookedSeats = useCallback(async () => {
    if (!selectedShow?.id) {
      setBookedSeatIds([]);
      setAvailabilityLoading(false);
      return;
    }

    setAvailabilityLoading(true);
    setAvailabilityError("");
    setSubmitMessage("");
    setSubmitError("");
    setSelectedSeats([]);

    const { data, error } = await supabase
      .from("seat_bookings")
      .select("seat_id")
      .eq("show_id", selectedShow.id);

    if (error) {
      console.error("Failed to fetch booked seats:", error);
      setBookedSeatIds([]);
      setAvailabilityError(
        "We could not load seat availability right now. Please try again.",
      );
      setAvailabilityLoading(false);
      return;
    }

    const seatIds = (data || []).map((row) => row.seat_id).filter(Boolean);
    setBookedSeatIds(seatIds);
    setAvailabilityLoading(false);
  }, [selectedShow]);

  useEffect(() => {
    fetchShows();
  }, [fetchShows]);

  useEffect(() => {
    if (activeTab === HUB_TABS.TICKETS) {
      fetchBookedSeats();
    }
  }, [activeTab, fetchBookedSeats]);

  useEffect(() => {
    if (selectedShow?.id && activeTab === HUB_TABS.TICKETS) {
      fetchBookedSeats();
    }
  }, [selectedShow?.id, activeTab, fetchBookedSeats]);

  useEffect(() => {
    const updateSeatViewport = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isPhoneLike = width <= 900;
      const isPortrait = height > width;

      setIsLandscapeRequired(
        activeTab === HUB_TABS.TICKETS && isPhoneLike && isPortrait,
      );

      const outerPagePadding = width < 640 ? 32 : width < 1024 ? 56 : 80;
      const sectionInnerPadding = width < 640 ? 24 : 40;
      const safeAvailableWidth = Math.max(
        width - outerPagePadding - sectionInnerPadding,
        320,
      );

      const baseSeatSize = 44;
      const baseGap = 8;
      const rowLabelWidth = 32;
      const fullSeatWidth =
        maxRowSeats * baseSeatSize +
        (maxRowSeats - 1) * baseGap +
        rowLabelWidth * 2 +
        24;

      const nextScale = Math.min(1, safeAvailableWidth / fullSeatWidth);
      setSeatScale(Number.isFinite(nextScale) ? Math.max(nextScale, 0.5) : 1);
    };

    updateSeatViewport();
    window.addEventListener("resize", updateSeatViewport);
    window.addEventListener("orientationchange", updateSeatViewport);

    return () => {
      window.removeEventListener("resize", updateSeatViewport);
      window.removeEventListener("orientationchange", updateSeatViewport);
    };
  }, [activeTab, maxRowSeats]);

  const handleShowChange = (e) => {
    setSelectedShowId(e.target.value);
  };

  const handleTicketInputChange = (e) => {
    const { name, value } = e.target;
    setTicketForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleReportInputChange = (e) => {
    const { name, value } = e.target;
    setReportForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const toggleSeat = (seatId) => {
    if (bookedSeatSet.has(seatId)) return;

    setSubmitMessage("");
    setSubmitError("");

    setSelectedSeats((prev) => {
      const exists = prev.includes(seatId);

      if (exists) {
        return prev.filter((seat) => seat !== seatId);
      }

      if (prev.length >= MAX_SEATS_PER_BOOKING) {
        setSubmitError(
          `A maximum of ${MAX_SEATS_PER_BOOKING} seats can be booked in one order.`,
        );
        return prev;
      }

      return sortSeatIds([...prev, seatId]);
    });
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setSubmitMessage("");
    setSubmitError("");

    const robloxUsername = ticketForm.robloxUsername.trim();
    const email = ticketForm.email.trim().toLowerCase();

    if (!selectedShow) {
      setSubmitError("Please select a show.");
      return;
    }

    if (!robloxUsername) {
      setSubmitError("Please enter your Roblox username.");
      return;
    }

    if (!email) {
      setSubmitError("Please enter your email address.");
      return;
    }

    if (!selectedSeats.length) {
      setSubmitError("Please select at least one seat.");
      return;
    }

    if (selectedSeats.length > MAX_SEATS_PER_BOOKING) {
      setSubmitError(
        `Bookings are limited to ${MAX_SEATS_PER_BOOKING} seats per order.`,
      );
      return;
    }

    setBookingLoading(true);

    const { data: latestCheck, error: latestCheckError } = await supabase
      .from("seat_bookings")
      .select("seat_id")
      .eq("show_id", selectedShow.id)
      .in("seat_id", selectedSeats);

    if (latestCheckError) {
      console.error("Seat re-check failed:", latestCheckError);
      setSubmitError(
        "We could not confirm seat availability. Please try again.",
      );
      setBookingLoading(false);
      return;
    }

    if ((latestCheck || []).length > 0) {
      const nowTaken = latestCheck.map((item) => item.seat_id).join(", ");
      setSubmitError(
        `Some seats were just booked by someone else: ${nowTaken}. Please choose different seats.`,
      );
      setBookingLoading(false);
      await fetchBookedSeats();
      return;
    }

    const { data: latestBookingRow, error: latestBookingError } = await supabase
      .from("seat_bookings")
      .select("booking_reference")
      .not("booking_reference", "is", null)
      .order("id", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (latestBookingError) {
      console.error(
        "Failed to get latest booking reference:",
        latestBookingError,
      );
      setSubmitError("Could not generate booking reference. Please try again.");
      setBookingLoading(false);
      return;
    }

    let nextNumber = 1;

    if (latestBookingRow?.booking_reference) {
      const match = latestBookingRow.booking_reference.match(/^OB(\d+)$/i);
      if (match) {
        nextNumber = Number(match[1]) + 1;
      }
    }

    const bookingReference = `OB${nextNumber}`;
    const ticketStatus = "Scheduled";

    const rowsToInsert = selectedSeats.map((seatId) => ({
      show_id: selectedShow.id,
      show_date: selectedShow.show_date,
      seat_id: seatId,
      roblox_username: robloxUsername,
      email,
      booking_reference: bookingReference,
      ticket_status: ticketStatus,
    }));

    const { error: insertError } = await supabase
      .from("seat_bookings")
      .insert(rowsToInsert);

    if (insertError) {
      console.error("Booking insert failed:", insertError);
      setSubmitError(
        "Your booking could not be completed right now. Please try again.",
      );
      setBookingLoading(false);
      return;
    }

    try {
      await fetch("/api/send-booking-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          to: email,
          robloxUsername,
          peopleAttending: selectedSeats.length,
          bookingReference,
          ticketStatus,
          seats: selectedSeats,
          showName: selectedShow.show_name,
          showDate: selectedShow.show_date,
          showTime: selectedShow.show_time,
        }),
      });
    } catch (emailError) {
      console.error("Email send request failed:", emailError);
    }

    setSubmitMessage(
      `Booking confirmed. Your reference is ${bookingReference}.`,
    );

    setTicketForm({
      robloxUsername: "",
      email: "",
    });
    setSelectedSeats([]);
    setBookingLoading(false);
    await fetchBookedSeats();
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    setContactMessage("");
    setContactError("");

    const fullName = contactForm.fullName.trim();
    const email = contactForm.email.trim().toLowerCase();
    const subject = contactForm.subject.trim();
    const message = contactForm.message.trim();

    if (!fullName) {
      setContactError("Please enter your full name.");
      return;
    }

    if (!email) {
      setContactError("Please enter your email address.");
      return;
    }

    if (!subject) {
      setContactError("Please enter a subject.");
      return;
    }

    if (!message) {
      setContactError("Please enter your message.");
      return;
    }

    setContactLoading(true);

    try {
      const response = await fetch("/api/contact-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName,
          email,
          subject,
          message,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || "Failed to submit contact form.");
      }

      setContactMessage(
        "Your message has been received. A confirmation email has been sent.",
      );
      setContactForm({
        fullName: "",
        email: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Contact submission failed:", error);
      setContactError(
        error.message || "We could not send your contact request right now.",
      );
    } finally {
      setContactLoading(false);
    }
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setReportMessage("");
    setReportError("");

    const payload = {
      reporter_name: reportForm.reporterName.trim(),
      reporter_email: reportForm.reporterEmail.trim().toLowerCase(),
      staff_member_name: reportForm.staffMemberName.trim(),
      incident_date: reportForm.incidentDate || null,
      category: reportForm.category.trim(),
      details: reportForm.details.trim(),
      evidence_links: reportForm.evidenceLinks.trim() || null,
    };

    if (!payload.reporter_name) {
      setReportError("Please enter your name.");
      return;
    }

    if (!payload.reporter_email) {
      setReportError("Please enter your email address.");
      return;
    }

    if (!payload.staff_member_name) {
      setReportError("Please enter the staff member name.");
      return;
    }

    if (!payload.category) {
      setReportError("Please select or enter a report category.");
      return;
    }

    if (!payload.details) {
      setReportError("Please provide the report details.");
      return;
    }

    setReportLoading(true);

    const { error } = await supabase.from("staff_reports").insert([payload]);

    if (error) {
      console.error("Failed to save staff report:", error);
      setReportError("We could not submit the staff report right now.");
      setReportLoading(false);
      return;
    }

    setReportMessage("Your staff report has been submitted successfully.");
    setReportForm({
      reporterName: "",
      reporterEmail: "",
      staffMemberName: "",
      incidentDate: "",
      category: "",
      details: "",
      evidenceLinks: "",
    });
    setReportLoading(false);
  };

  const tabButtonClass = (tab) =>
    [
      "inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition",
      activeTab === tab
        ? "border-cyan-200/40 bg-cyan-300 text-slate-950 shadow-[0_10px_30px_rgba(103,232,249,0.25)]"
        : "border-white/10 bg-white/[0.06] text-white hover:border-white/20 hover:bg-white/[0.10]",
    ].join(" ");

  const baseSeatSize = 44;
  const baseGap = 8;
  const baseRowWidth = maxRowSeats * baseSeatSize + (maxRowSeats - 1) * baseGap;
  const rowLabelWidth = 32;
  const fullSeatLayoutWidth = baseRowWidth + rowLabelWidth * 2 + 24;
  const fullSeatLayoutHeight = seatingLayout.length * (baseSeatSize + 8) - 8;

  return (
    <>
      <Header />
      <NoticeBar />

      <div className="min-h-screen bg-[#08111f] text-white">
        <div className="pointer-events-none fixed inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(135deg,#4a0454_0%,#3c2f73_35%,#255a86_68%,#12728a_100%)] opacity-90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_26%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.05),transparent_24%)]" />
          <div className="absolute inset-0 bg-black/30" />
        </div>

        <div className="relative mx-auto max-w-[1700px] px-4 py-5 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
          <section className="mb-8 overflow-hidden rounded-[30px] border border-white/10 bg-white/[0.06] shadow-[0_22px_90px_rgba(0,0,0,0.32)] backdrop-blur-xl">
            <img
              src={BANNER_URL}
              alt="Benton Theatres"
              className="h-auto w-full object-cover"
            />
          </section>

          <section className="overflow-hidden rounded-[30px] border border-white/10 bg-[linear-gradient(135deg,rgba(86,24,113,0.45),rgba(39,78,125,0.45),rgba(18,111,135,0.40))] shadow-[0_20px_80px_rgba(0,0,0,0.32)] backdrop-blur-xl">
            <div className="grid gap-8 px-5 py-6 sm:px-8 sm:py-8 lg:grid-cols-[1.2fr_0.8fr] lg:px-10 lg:py-10">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.24em] text-white/80">
                  <FaInfoCircle />
                  <span>{HUB_SUBTITLE}</span>
                </div>

                <h1 className="text-3xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                  {SHOW_TITLE}
                </h1>

                <p className="mt-4 max-w-2xl text-sm leading-7 text-white/80 sm:text-base lg:text-lg">
                  Access the Benton support and booking hub. Contact the team,
                  report a staff member, or reserve theatre seats through the live
                  performance booking system.
                </p>

                <div className="mt-6 grid gap-3 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setActiveTab(HUB_TABS.CONTACT)}
                    className={tabButtonClass(HUB_TABS.CONTACT)}
                  >
                    <FaPhoneAlt />
                    Contact page
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab(HUB_TABS.REPORT)}
                    className={tabButtonClass(HUB_TABS.REPORT)}
                  >
                    <FaUserShield />
                    Report a staff member
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab(HUB_TABS.TICKETS)}
                    className={tabButtonClass(HUB_TABS.TICKETS)}
                  >
                    <FaTicketAlt />
                    Ticket booking
                  </button>
                </div>
              </div>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur-xl sm:p-5">
                <div className="mb-4 flex items-center gap-2 text-sm font-medium text-white/90">
                  <FaInfoCircle />
                  <span>Hub information</span>
                </div>

                <div className="space-y-3 text-sm leading-6 text-white/78">
                  <p>Use the contact page for general enquiries and support.</p>
                  <p>
                    Use the staff report page to log incidents for internal
                    review.
                  </p>
                  <p>
                    Use the ticket booking page to reserve seats for available
                    performances.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {activeTab === HUB_TABS.CONTACT ? (
            <div className="mt-8 grid gap-8 xl:grid-cols-[1fr_0.9fr]">
              <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                    Contact form
                  </div>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                    Get in touch
                  </h2>
                </div>

                {contactError ? (
                  <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span>{contactError}</span>
                  </div>
                ) : null}

                {contactMessage ? (
                  <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                    <FaCheckCircle className="mt-0.5 shrink-0" />
                    <span>{contactMessage}</span>
                  </div>
                ) : null}

                <form onSubmit={handleSubmitContact} className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Full Name
                    </label>
                    <div className="relative">
                      <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="text"
                        name="fullName"
                        value={contactForm.fullName}
                        onChange={handleContactInputChange}
                        placeholder="Enter your full name"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Email Address
                    </label>
                    <div className="relative">
                      <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={handleContactInputChange}
                        placeholder="Enter your email address"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Subject
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleContactInputChange}
                      placeholder="What is your message about?"
                      className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactInputChange}
                      rows={7}
                      placeholder="Enter your message"
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={contactLoading}
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {contactLoading ? "Sending message..." : "Submit contact form"}
                  </button>
                </form>
              </section>

              <aside className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                    Contact notes
                  </div>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                    Before you send
                  </h2>
                </div>

                <div className="space-y-4 rounded-[24px] border border-white/10 bg-white/[0.06] p-5 text-sm leading-7 text-white/78">
                  <p>
                    Use this form for support, general enquiries, or follow-up
                    questions.
                  </p>
                  <p>
                    A confirmation email will be sent to the address you enter.
                  </p>
                  <p>
                    Your contact request is stored in Supabase for later review by
                    staff.
                  </p>
                </div>
              </aside>
            </div>
          ) : null}

          {activeTab === HUB_TABS.REPORT ? (
            <div className="mt-8 grid gap-8 xl:grid-cols-[1fr_0.9fr]">
              <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                    Staff report form
                  </div>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                    Report a staff member
                  </h2>
                </div>

                {reportError ? (
                  <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span>{reportError}</span>
                  </div>
                ) : null}

                {reportMessage ? (
                  <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                    <FaCheckCircle className="mt-0.5 shrink-0" />
                    <span>{reportMessage}</span>
                  </div>
                ) : null}

                <form onSubmit={handleSubmitReport} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-white/82">
                        Your Name
                      </label>
                      <input
                        type="text"
                        name="reporterName"
                        value={reportForm.reporterName}
                        onChange={handleReportInputChange}
                        placeholder="Enter your name"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-white/82">
                        Your Email
                      </label>
                      <input
                        type="email"
                        name="reporterEmail"
                        value={reportForm.reporterEmail}
                        onChange={handleReportInputChange}
                        placeholder="Enter your email"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-white/82">
                        Staff Member Name
                      </label>
                      <input
                        type="text"
                        name="staffMemberName"
                        value={reportForm.staffMemberName}
                        onChange={handleReportInputChange}
                        placeholder="Who are you reporting?"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-white/82">
                        Incident Date
                      </label>
                      <input
                        type="date"
                        name="incidentDate"
                        value={reportForm.incidentDate}
                        onChange={handleReportInputChange}
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition focus:border-cyan-300/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Report Category
                    </label>
                    <select
                      name="category"
                      value={reportForm.category}
                      onChange={handleReportInputChange}
                      className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition focus:border-cyan-300/40"
                    >
                      <option value="" className="bg-[#142138] text-white">
                        Select a category
                      </option>
                      <option value="Conduct" className="bg-[#142138] text-white">
                        Conduct
                      </option>
                      <option
                        value="Abuse of power"
                        className="bg-[#142138] text-white"
                      >
                        Abuse of power
                      </option>
                      <option
                        value="Harassment"
                        className="bg-[#142138] text-white"
                      >
                        Harassment
                      </option>
                      <option value="Other" className="bg-[#142138] text-white">
                        Other
                      </option>
                    </select>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Details
                    </label>
                    <textarea
                      name="details"
                      value={reportForm.details}
                      onChange={handleReportInputChange}
                      rows={7}
                      placeholder="Provide full details of what happened"
                      className="w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 py-3 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Evidence Links
                    </label>
                    <input
                      type="text"
                      name="evidenceLinks"
                      value={reportForm.evidenceLinks}
                      onChange={handleReportInputChange}
                      placeholder="Optional links to screenshots, clips, or evidence"
                      className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] px-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={reportLoading}
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {reportLoading ? "Submitting report..." : "Submit staff report"}
                  </button>
                </form>
              </section>

              <aside className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                    Reporting notes
                  </div>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                    Internal review
                  </h2>
                </div>

                <div className="space-y-4 rounded-[24px] border border-white/10 bg-white/[0.06] p-5 text-sm leading-7 text-white/78">
                  <p>
                    Use this form to log a complaint or incident involving a staff
                    member.
                  </p>
                  <p>
                    Include as much detail as possible so it can be reviewed
                    properly.
                  </p>
                  <p>
                    Reports are saved in Supabase for later access by your admin
                    team.
                  </p>
                </div>
              </aside>
            </div>
          ) : null}

          {activeTab === HUB_TABS.TICKETS ? (
            <div className="mt-8 grid gap-8 xl:grid-cols-[1.2fr_0.8fr]">
              <section className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                      Performance selection
                    </div>
                    <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                      Seating plan
                    </h2>
                  </div>

                  <div className="w-full max-w-[360px]">
                    <label className="mb-2 block text-sm font-medium text-white/80">
                      Performance date
                    </label>
                    <div className="relative">
                      <FaRegCalendarAlt className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/45" />
                      <select
                        value={selectedShowId}
                        onChange={handleShowChange}
                        disabled={showsLoading || shows.length === 0}
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition focus:border-cyan-300/40 disabled:opacity-60"
                      >
                        {shows.length === 0 ? (
                          <option value="">No shows available</option>
                        ) : (
                          shows.map((show) => (
                            <option
                              key={show.id}
                              value={show.id}
                              className="bg-[#142138] text-white"
                            >
                              {show.show_name} - {formatDisplayDate(show.show_date)} -{" "}
                              {formatTimeDisplay(show.show_time)}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-5 rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),rgba(255,255,255,0.04))] px-4 py-4 text-center shadow-inner">
                  <div className="mx-auto mb-2 h-2 w-full max-w-[700px] rounded-full bg-[linear-gradient(90deg,rgba(118,14,131,0.65),rgba(72,112,210,0.85),rgba(36,151,170,0.85))]" />
                  <div className="text-xs uppercase tracking-[0.34em] text-white/65">
                    Stage
                  </div>
                </div>

                <div className="mb-5 flex flex-wrap gap-3 text-xs sm:text-sm">
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-2 text-white/80">
                    <span className="h-3 w-3 rounded-full bg-white/90" />
                    Available
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-2 text-white/80">
                    <span className="h-3 w-3 rounded-full bg-cyan-300" />
                    Selected
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-2 text-white/80">
                    <span className="h-3 w-3 rounded-full bg-fuchsia-500" />
                    Booked
                  </div>
                </div>

                {availabilityError ? (
                  <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span>{availabilityError}</span>
                  </div>
                ) : null}

                {isLandscapeRequired ? (
                  <div className="grid min-h-[320px] place-items-center rounded-[24px] border border-white/10 bg-[rgba(7,15,28,0.55)] px-6 py-10 text-center">
                    <div className="max-w-[340px]">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-cyan-300/20 bg-cyan-500/10 text-cyan-200">
                        <FaMobileAlt className="text-2xl" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">
                        Rotate your device
                      </h3>
                      <p className="mt-3 text-sm leading-7 text-white/75">
                        The seating plan is only shown in landscape on mobile so
                        the full layout fits on screen properly.
                      </p>
                    </div>
                  </div>
                ) : availabilityLoading ? (
                  <div className="grid min-h-[300px] place-items-center rounded-[24px] border border-white/10 bg-white/[0.04] text-sm text-white/70">
                    Loading seat availability...
                  </div>
                ) : (
                  <div className="rounded-[24px] border border-white/10 bg-[rgba(7,15,28,0.55)] px-2 py-3 sm:px-4 sm:py-4">
                    <div className="overflow-auto">
                      <div
                        style={{
                          width: `${fullSeatLayoutWidth * seatScale}px`,
                          height: `${fullSeatLayoutHeight * seatScale}px`,
                          minWidth: `${fullSeatLayoutWidth * seatScale}px`,
                          margin: "0 auto",
                        }}
                      >
                        <div
                          style={{
                            width: `${fullSeatLayoutWidth}px`,
                            height: `${fullSeatLayoutHeight}px`,
                            transform: `scale(${seatScale})`,
                            transformOrigin: "top left",
                          }}
                        >
                          <div className="space-y-2">
                            {seatingLayout.map((row) => (
                              <div
                                key={row.row}
                                className="grid items-center gap-3"
                                style={{
                                  gridTemplateColumns: `${rowLabelWidth}px ${baseRowWidth}px ${rowLabelWidth}px`,
                                  justifyContent: "center",
                                }}
                              >
                                <div className="flex h-11 items-center justify-center text-sm font-semibold text-white/60">
                                  {row.row}
                                </div>

                                <div
                                  className="grid gap-2"
                                  style={{
                                    gridTemplateColumns: `repeat(${row.seats.length}, ${baseSeatSize}px)`,
                                    justifyContent: "center",
                                    width: `${baseRowWidth}px`,
                                    margin: "0 auto",
                                  }}
                                >
                                  {row.seats.map((seatId) => {
                                    const isBooked = bookedSeatSet.has(seatId);
                                    const isSelected = selectedSeatSet.has(seatId);

                                    return (
                                      <button
                                        key={seatId}
                                        type="button"
                                        onClick={() => toggleSeat(seatId)}
                                        disabled={isBooked}
                                        className={[
                                          "group relative flex h-11 w-11 items-center justify-center rounded-xl border text-[10px] font-semibold transition",
                                          isBooked
                                            ? "cursor-not-allowed border-fuchsia-400/30 bg-fuchsia-600 text-white/95"
                                            : isSelected
                                              ? "border-cyan-200/40 bg-cyan-300 text-slate-950 shadow-[0_8px_30px_rgba(103,232,249,0.28)]"
                                              : "border-white/10 bg-white text-slate-950 hover:scale-[1.02] hover:border-cyan-200/30",
                                        ].join(" ")}
                                        title={
                                          isBooked
                                            ? `${seatId} is already booked`
                                            : isSelected
                                              ? `${seatId} selected`
                                              : `Select ${seatId}`
                                        }
                                      >
                                        <span className="absolute left-1 top-1 opacity-70">
                                          <FaChair className="h-2 w-2" />
                                        </span>
                                        <span>{seatId}</span>
                                        {isBooked ? (
                                          <span className="absolute right-1 top-1 opacity-90">
                                            <FaLock className="h-2 w-2" />
                                          </span>
                                        ) : null}
                                      </button>
                                    );
                                  })}
                                </div>

                                <div className="flex h-11 items-center justify-center text-sm font-semibold text-white/60">
                                  {row.row}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <aside className="rounded-[30px] border border-white/10 bg-[rgba(17,26,43,0.72)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.30)] backdrop-blur-xl sm:p-6 lg:p-7">
                <div className="mb-6">
                  <div className="text-[11px] uppercase tracking-[0.24em] text-white/50">
                    Booking form
                  </div>
                  <h2 className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">
                    Reserve your seats
                  </h2>
                </div>

                <div className="mb-5 rounded-[24px] border border-white/10 bg-white/[0.06] p-4">
                  <div className="mb-3 text-sm font-medium text-white/90">
                    Booking summary
                  </div>

                  <div className="space-y-2 text-sm text-white/75">
                    <div className="flex items-center justify-between gap-4">
                      <span>Show</span>
                      <span className="text-right font-medium text-white">
                        {selectedShow?.show_name || "None"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <span>Date</span>
                      <span className="text-right font-medium text-white">
                        {selectedShow
                          ? `${formatDisplayDate(selectedShow.show_date)} • ${formatTimeDisplay(selectedShow.show_time)}`
                          : "None"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <span>Seats selected</span>
                      <span className="font-medium text-white">
                        {selectedSeats.length}
                      </span>
                    </div>

                    <div className="flex items-start justify-between gap-4">
                      <span>Seat list</span>
                      <span className="max-w-[220px] text-right font-medium text-white">
                        {selectedSeats.length ? selectedSeats.join(", ") : "None"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <span>Available now</span>
                      <span className="font-medium text-white">
                        {availableSeatCount} seats
                      </span>
                    </div>
                  </div>
                </div>

                {submitError ? (
                  <div className="mb-4 flex items-start gap-3 rounded-2xl border border-red-400/20 bg-red-500/10 px-4 py-4 text-sm text-red-100">
                    <FaExclamationTriangle className="mt-0.5 shrink-0" />
                    <span>{submitError}</span>
                  </div>
                ) : null}

                {submitMessage ? (
                  <div className="mb-4 flex items-start gap-3 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-100">
                    <FaCheckCircle className="mt-0.5 shrink-0" />
                    <span>{submitMessage}</span>
                  </div>
                ) : null}

                <form onSubmit={handleSubmitBooking} className="space-y-4">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Roblox Username
                    </label>
                    <div className="relative">
                      <FaUser className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="text"
                        name="robloxUsername"
                        value={ticketForm.robloxUsername}
                        onChange={handleTicketInputChange}
                        placeholder="Enter your Roblox username"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Your Email
                    </label>
                    <div className="relative">
                      <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="email"
                        name="email"
                        value={ticketForm.email}
                        onChange={handleTicketInputChange}
                        placeholder="Enter your email address"
                        className="h-12 w-full rounded-2xl border border-white/10 bg-white/[0.08] pl-12 pr-4 text-sm text-white outline-none transition placeholder:text-white/35 focus:border-cyan-300/40"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-white/82">
                      Seats Selected
                    </label>
                    <input
                      type="text"
                      value={selectedSeats.join(", ")}
                      readOnly
                      placeholder="Your selected seats will appear here"
                      className="h-12 w-full cursor-not-allowed rounded-2xl border border-white/10 bg-white/[0.06] px-4 text-sm text-white/92 outline-none placeholder:text-white/35"
                    />
                    <p className="mt-2 text-xs text-white/50">
                      This field is filled automatically from your seating
                      selection.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={
                      bookingLoading ||
                      availabilityLoading ||
                      !selectedShow ||
                      !selectedSeats.length ||
                      selectedSeats.length > MAX_SEATS_PER_BOOKING ||
                      isLandscapeRequired
                    }
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[linear-gradient(90deg,#6a1b9a,#3c67d6,#1aa4b8)] px-5 text-sm font-semibold text-white shadow-[0_12px_32px_rgba(26,164,184,0.25)] transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {bookingLoading ? "Processing booking..." : "Confirm booking"}
                  </button>
                </form>
              </aside>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}